import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Search } from 'lucide-react';
import { DecisionBadge, GovernanceBreadcrumbs, RiskBadge, btnSecondary, inputClass, selectClass } from '../components/shared';
import { aiGovernanceService, AuditLogItem, AuditStats } from '../../../../components/api/aiGovernanceService';

export default function AuditLogsScreen() {
  const [search, setSearch] = useState('');
  const [decisionFilter, setDecisionFilter] = useState('All');
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, statsRes] = await Promise.all([
        aiGovernanceService.getAuditLogs({
          search: search || undefined,
          decision: decisionFilter !== 'All' ? decisionFilter : undefined,
          page: 1,
          pageSize: 50,
        }),
        aiGovernanceService.getAuditStats('24h'),
      ]);
      setLogs(list.items || []);
      setStats(statsRes);
    } catch {
      setError('Failed to load audit logs.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [search, decisionFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    try {
      const blob = await aiGovernanceService.exportAuditLogs({
        search: search || undefined,
        decision: decisionFilter !== 'All' ? decisionFilter : undefined,
        format: 'csv',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `governance-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export audit logs.');
    }
  };

  return (
    <div className="space-y-4">
      <GovernanceBreadcrumbs items={[{ label: 'AI Governance' }, { label: 'Audit Logs' }]} />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Audit Logs</h1>
          <p className="text-xs text-slate-500 mt-0.5">Complete audit trail of policy decisions, tool invocations, and enforcement actions.</p>
        </div>
        <button onClick={handleExport} className={btnSecondary}>Export Logs</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: 'Total Events (24h)', value: stats?.totalEvents?.toLocaleString() ?? '—' },
          { label: 'Blocked Requests', value: stats?.blockedRequests?.toLocaleString() ?? '—' },
          { label: 'Approvals Pending', value: stats?.approvalsPending?.toLocaleString() ?? '—' },
          { label: 'Mask Actions', value: stats?.maskActions?.toLocaleString() ?? '—' },
        ].map((card) => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 shadow-sm">
            <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{card.label}</p>
            <p className="text-xl font-semibold text-slate-900 mt-0.5">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by user or resource..." className={`${inputClass} pl-8`} />
        </div>
        <select value={decisionFilter} onChange={(e) => setDecisionFilter(e.target.value)} className={`${selectClass} w-40`}>
          <option value="All">All Decisions</option>
          <option>Allow</option><option>Mask</option><option>Deny</option><option>Approval Required</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {['Timestamp', 'User', 'Resource', 'Policy', 'Decision', 'Risk Level', 'Tool Invoked', 'Model Used', ''].map((h) => (
                <th key={h || 'actions'} className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500">No audit events found.</td></tr>
            ) : logs.map((log) => (
              <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                <td className="px-3 py-2 text-slate-500 font-mono text-[10px] whitespace-nowrap">{log.timestamp}</td>
                <td className="px-3 py-2 text-slate-700">{log.user}</td>
                <td className="px-3 py-2 text-slate-700">{log.resource}</td>
                <td className="px-3 py-2 text-indigo-700 font-medium">{log.policy}</td>
                <td className="px-3 py-2"><DecisionBadge decision={log.decision} /></td>
                <td className="px-3 py-2"><RiskBadge level={log.riskLevel} /></td>
                <td className="px-3 py-2 text-slate-600 font-mono text-[10px]">{log.toolInvoked}</td>
                <td className="px-3 py-2 text-slate-600">{log.modelUsed}</td>
                <td className="px-3 py-2">
                  <button onClick={() => setSelectedLog(log)} className="flex items-center gap-1 text-[10px] font-medium text-indigo-600 hover:text-indigo-800">
                    <Eye className="w-3 h-3" /> Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedLog && (
        <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Audit Event Details</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Timestamp</span><span className="text-slate-800 font-mono">{selectedLog.timestamp}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">User</span><span className="text-slate-800">{selectedLog.user}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Resource</span><span className="text-slate-800">{selectedLog.resource}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Policy</span><span className="text-indigo-700">{selectedLog.policy}</span></div>
              <div className="flex justify-between items-center"><span className="text-slate-500">Decision</span><DecisionBadge decision={selectedLog.decision} /></div>
              <div className="flex justify-between items-center"><span className="text-slate-500">Risk Level</span><RiskBadge level={selectedLog.riskLevel} /></div>
              <div className="pt-2 border-t border-slate-100">
                <p className="text-slate-500 mb-1">Details</p>
                <p className="text-slate-700 leading-relaxed">{selectedLog.details}</p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setSelectedLog(null)} className={btnSecondary}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
